import { useContext, useMemo } from 'react';
import { KitProduct } from '../../../../../../types/kit_template/kitTemplate';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { useToast } from '../../../../../../providers/ToastProvider';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { t, translate } from '../../../../../../types/translation/Translator';
import { KitTemplateInput } from '../../../../../../types/kit_template/kitTemplate.input';
import { Button, ButtonVariant } from '../../../../../components/Button';
import React from 'react';
import Table, { TableHeader } from '../../../../../components/Table';
import { TextInput } from '../../../../../components/TextInput';
import { DeleteIconButton } from '../../../../../icons';
import { ActionModal } from '../../../../../components/Modal';
import { DefaultDropdownItem, DropdownSearchSelect } from '../../../../../components/Dropdown';
import { UnitOfMeasureUtil } from '../../../../../../types/uom';
import PackageIcon from '../../../../../../VentoryUI/icons/Package/PackagIcon';

export interface KitTemplateInfoPaneProps {
  input: KitTemplateInput;
  setInput: (input: KitTemplateInput) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  loading: boolean;

  onCreate: () => Promise<void>;
  onUpdate: () => Promise<void>;
}

export default function KitTemplateInfoPane({
  input,
  setInput,
  open,
  setOpen,
  onCreate,
  onUpdate,
  loading,
}: KitTemplateInfoPaneProps) {
  const { productMasterData, productMasterDataLoading } = useContext(ProductMasterDataContext);
  const { showToast } = useToast();

  const templatePmdIds = useMemo(
    () => new Set([...input.products.map(p => p.productMasterDataId), input.productMasterDataId]),
    [input],
  );

  const handleAction = async () => {
    if (input.id) {
      await onUpdate();
    } else {
      await onCreate();
    }
  };

  const handleProductDelete = (item: KitProduct) => {
    setInput(input.withoutProduct(item.productMasterDataId));
  };

  const actionText = input.id ? t(TranslationKey.save).singular.upper : t(TranslationKey.create).singular.upper;

  const footer = (
    <div className='flex justify-end gap-2'>
      <Button
        onClick={() => setOpen(false)}
        variant={ButtonVariant.secondary}
        text={t(TranslationKey.done).singular.label}
        disabled={loading}
      />
      <Button
        onClick={handleAction}
        text={actionText}
        disabled={!input.name || !input.products.length}
        loading={loading}
      />
    </div>
  );

  const headers: TableHeader<KitProduct>[] = [
    {
      key: 'productName',
      name: t(TranslationKey.productName).singular.label,
      text: (item: KitProduct) => productMasterData.get(item.productMasterDataId)?.productName || '',
    },
    {
      key: 'productNumber',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: KitProduct) => productMasterData.get(item.productMasterDataId)?.productNumber || '',
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: KitProduct) => {
        return (
          <div className='flex h-7'>
            <TextInput
              onChange={v => {
                setInput(input.withProductQuantity(item.productMasterDataId, parseFloat(v) || 0));
              }}
              value={item.quantity.toString()}
              type='number'
              className='h-7'
              trailing={
                UnitOfMeasureUtil.getSuffix(productMasterData.get(item.productMasterDataId || '')?.unitOfMeasure) || ' '
              }
            />
          </div>
        );
      },
    },
    {
      key: 'delete',
      column: '1',
      name: '',
      text: (item: KitProduct) => <DeleteIconButton onClick={() => handleProductDelete(item)} />,
    },
  ];

  const titleText = input.id
    ? t(TranslationKey.kitTemplateCreateTitle).singular.upper
    : t(TranslationKey.kitTemplateUpdateTitle).singular.upper;

  return (
    <ActionModal title={titleText} open={open} setOpen={setOpen} footer={footer} width='300px'>
      <div className='grid min-w-[400px] gap-2'>
        <TextInput
          disabled={true}
          label={t(TranslationKey.productReferenceData).singular.label}
          value={productMasterData.get(input.productMasterDataId)?.productName}
          onChange={() => {}}
        />
        <TextInput
          disabled={false}
          mandatory={true}
          label={t(TranslationKey.name).singular.label}
          value={input.name || ''}
          onChange={value => {
            setInput(input.withName(value));
          }}
        />
        <DropdownSearchSelect
          disabled={productMasterDataLoading}
          label={t(TranslationKey.product).plural.label}
          placeholder={t(TranslationKey.product).plural.label}
          items={[...productMasterData.values()]
            .filter(item => !item.kitManaged && !templatePmdIds.has(item.id))
            .map(item => item.id)}
          textBuilder={(id: string) => {
            const pmd = productMasterData.get(id);
            if (!pmd) return translate({ key: TranslationKey.unknownProductMasterData });
            return `${pmd.productName} - ${pmd.productNumber}`;
          }}
          itemBuilder={id => {
            const pmd = productMasterData.get(id);
            if (!pmd) return translate({ key: TranslationKey.unknownProductMasterData });
            return <DefaultDropdownItem leading={<PackageIcon />} text={pmd.productName} subtext={pmd.productNumber} />;
          }}
          value={undefined}
          onSelect={id => {
            if (!id) return;
            setInput(input.withProduct(new KitProduct(id, 1)));
          }}
        />
        <div className='h-full min-h-64'>
          <Table headers={headers} items={input.products} />
        </div>
      </div>
    </ActionModal>
  );
}
